import { useEffect, useRef, useState } from 'react';

import { ErrorScreenWrapper } from '../error';

interface LoadScriptProps {
  async?: boolean;
  defer?: boolean;
  isShowError?: boolean;
  onload?: ((this: GlobalEventHandlers, ev: Event) => any) | null;
  onRetry?: () => void;
  src: string;
  type?: 'module' | 'text/javascript';
}

const LoadScript = ({
  async = true,
  defer = false,
  isShowError = false,
  onload = null,
  src,
  type = 'text/javascript',
  onRetry,
}: LoadScriptProps) => {
  const [isErrorLoadingScript, setIsErrorLoadingScript] = useState(false);

  // Keep onload out of the effect deps: a new callback identity per parent
  // render would otherwise remove and re-append the script tag, re-firing
  // onload on every render.
  const onloadRef = useRef(onload);
  onloadRef.current = onload;

  useEffect(() => {
    if (document.querySelector(`script[src="${src}"]`)) {
      return undefined;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = async;
    script.type = type;
    script.defer = defer;
    script.onload = function (ev) {
      onloadRef.current?.call(this, ev);
    };
    document.body.appendChild(script);

    script.onerror = () => {
      if (isShowError) {
        setIsErrorLoadingScript(true);
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [src, async, type, defer, isShowError]);

  return (
    <ErrorScreenWrapper
      systemErrorProps={{
        isError: isErrorLoadingScript,
        systemErrorScreenProps: {
          onRetry,
        },
      }}
    >
      {null}
    </ErrorScreenWrapper>
  );
};

export default LoadScript;
